/* eslint-disable */
import React, { useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Drawer,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Box,
  Typography,
  IconButton,
} from "@material-ui/core";
import Button from "components/CustomButtons/Button";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faArrowDown,
  faArrowUp,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { formatNumber } from "utils/formatNumberUtils.js";
import AuthContext from "contexts/AuthProvider.js";
import { parseHexColor, getAlphaColor } from "utils/colorUtils.js";
import { ThreeDots } from "react-loader-spinner";
import PerfectScrollbar from "react-perfect-scrollbar";

// SEU utils (adaptado):
import {
  buildHierarchyFromInitiatives,
  sortDataRecursively,
} from "../../../../utils/hierarchyUtils.js";
import { client } from "services/axiosClient.js";

// =============== CONSTANTS / UTILS =================

const circleSize = 30;

function getStatusStyles(status, isHovered) {
  let baseStyle = {};
  switch (status) {
    case "green":
      baseStyle = {
        background: "linear-gradient(135deg, #66bb6a 0%, #43a047 100%)",
        boxShadow: "0 0 10px rgba(67,160,71,0.4)",
      };
      break;
    case "yellow":
      baseStyle = {
        background: "linear-gradient(135deg, #ffd54f 0%, #ffc107 100%)",
        boxShadow: "0 0 10px rgba(255,193,7,0.4)",
      };
      break;
    case "red":
      baseStyle = {
        background: "linear-gradient(135deg, #ef5350 0%, #e53935 100%)",
        boxShadow: "0 0 10px rgba(229,57,53,0.4)",
      };
      break;
    default:
      baseStyle = {
        background: "linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)",
        boxShadow: "0 0 10px rgba(158,158,158,0.4)",
      };
  }
  return {
    width: circleSize,
    height: circleSize,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: 6,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    transform: isHovered ? "scale(1.3)" : "scale(1)",
    cursor: "pointer",
    ...baseStyle,
  };
}

// =============== STAGEOVERVIEW ===============
function StageOverview({
  initiatives,
  stageChoices,
  unit = "",
  showUnitBefore = false,
  onInitiativeClick,
  initiativeDetailsOpen = false,
}) {
  const { t } = useTranslation();
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#2196f3";
  const [hoveredItem, setHoveredItem] = useState(null); // hover de QUALQUER nível
  const [loadingInitId, setLoadingInitId] = useState(null); // spinner interno

  const allStages = React.useMemo(() => {
    // Get all stages present in the initiatives
    const stagesFromInitiatives = new Set(
      initiatives.map((init) => init.stage || "Draft")
    );
    if (stageChoices && stageChoices.length > 0) {
      // Order stages based on the provided stageChoices (using display values) and filter only those present
      return stageChoices
        .map((choice) => choice.display)
        .filter((stage) => stagesFromInitiatives.has(stage));
    }
    return Array.from(stagesFromInitiatives);
  }, [initiatives, stageChoices]);

  // Montar estatísticas
  const stageStats = React.useMemo(() => {
    const stats = {};
    allStages.forEach((st) => {
      stats[st] = { count: 0, totalKpi: 0 };
    });
    initiatives.forEach((init) => {
      const st = init.stage || "Draft";
      stats[st].count++;
      stats[st].totalKpi += init.kpiValue || 0;
    });
    return stats;
  }, [allStages, initiatives]);

  // soma total de kpiValue
  const totalKpiAllStages = allStages.reduce(
    (acc, st) => acc + stageStats[st].totalKpi,
    0
  );

  const [stageDrawerOpen, setStageDrawerOpen] = React.useState(false);
  const [selectedStage, setSelectedStage] = React.useState(null);
  const [stageInits, setStageInits] = React.useState([]);

  React.useEffect(() => {
    if (initiativeDetailsOpen) {
      setStageDrawerOpen(false); // fecha ao abrir detalhe
    } else if (!initiativeDetailsOpen && selectedStage) {
      setStageDrawerOpen(true); // reabre ao fechar detalhe
    }
  }, [initiativeDetailsOpen, selectedStage]);

  const handleStageClick = (stage) => {
    // filtra as initiatives
    const inThisStage = initiatives.filter(
      (init) => (init.stage || "Draft") === stage
    );
    setSelectedStage(stage);
    setStageInits(inThisStage);
    setStageDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setStageDrawerOpen(false);
    setSelectedStage(null);
    setStageInits([]);
  };

  const handleInitClickWrapper = async (id) => {
    setLoadingInitId(id); // exibe spinner
    if (onInitiativeClick) {
      // Se onInitiativeClick devolve uma Promise, esperamos
      const maybePromise = onInitiativeClick(id);
      if (maybePromise && typeof maybePromise.then === "function") {
        try {
          await maybePromise;
        } finally {
          setLoadingInitId(null); // desliga spinner quando finalizar
        }
      } else {
        // Se não for Promise, desligamos imediatamente
        setLoadingInitId(null);
      }
    } else {
      setLoadingInitId(null);
    }
  };

  const { r: baseR, g: baseG, b: baseB } = parseHexColor(clientColor);

  function getAlphaForStage(index, total) {
    if (total <= 1) return 1;
    const minAlpha = 0.4;
    const alphaRange = 1 - minAlpha;
    const step = alphaRange / (total - 1);
    return minAlpha + index * step;
  }

  const MAX_BAR_WIDTH = 600;

  return (
    <div style={{ padding: "16px", marginTop: "30px" }}>
      <div style={{ display: "flex", justifyContent: "middle" }}>
        <div style={{ width: "100%", maxWidth: "800px" }}>
          {allStages.map((stage, idx) => {
            const { count, totalKpi } = stageStats[stage];
            if (totalKpiAllStages === 0) {
              return (
                <div key={stage} style={{ width: "100%", marginBottom: "8px" }}>
                  <div
                    style={{
                      width: "100%",
                      textAlign: "left",
                      backgroundColor: "#f0f0f0",
                      padding: "14px",
                      borderRadius: "6px",
                    }}
                  >
                    {stage} (0)
                  </div>
                </div>
              );
            }
            const fraction = totalKpi / totalKpiAllStages;
            const barWidth = Math.round(fraction * MAX_BAR_WIDTH);
            const alpha = getAlphaForStage(idx, allStages.length);
            const backgroundColor = `rgba(${baseR}, ${baseG}, ${baseB}, ${alpha})`;

            return (
              <div
                key={stage}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  borderBottom:
                    idx < allStages.length - 1 ? "1px dotted #ccc" : "none",
                  paddingBottom: idx < allStages.length - 1 ? "8px" : "0",
                  marginBottom: "12px",
                }}
              >
                <div style={{ minWidth: "160px", marginRight: "8px" }}>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {stage} ({count})
                  </Typography>
                </div>

                {/* Container centralizado para a barra */}
                <div
                  style={{
                    width: `${MAX_BAR_WIDTH}px`,
                    display: "flex",
                    justifyContent: "center",
                    marginRight: "8px", // caso queira espaçamento à direita
                  }}
                >
                  <div
                    onClick={() =>
                      totalKpi > 0 ? handleStageClick(stage) : null
                    }
                    style={{
                      width: `${barWidth}px`,
                      backgroundColor,
                      padding: "14px 0",
                      borderRadius: "6px",
                      transition: "width 0.3s ease-in-out",
                      boxShadow: "0 0 6px rgba(0,0,0,0.2)",
                      cursor: totalKpi > 0 ? "pointer" : "default",
                      textAlign: "center",
                    }}
                  />
                </div>

                <div
                  style={{
                    marginLeft: "8px",
                    minWidth: "120px",
                    textAlign: "right",
                  }}
                >
                  <Typography
                    variant="body2"
                    style={{ fontWeight: 500, fontSize: "0.8rem" }}
                  >
                    {formatNumber(totalKpi, unit, showUnitBefore)}
                  </Typography>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Modern section for displaying Total */}
      <Box mt={1} display="flex" justifyContent="flex-end">
        <Paper
          elevation={2}
          style={{
            padding: "4px 12px", // Modern, compact padding
            borderRadius: "8px",
            minWidth: "150px",
            background: "linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)", // Subtle gradient
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="subtitle2"
            style={{ fontWeight: 600, color: "#333" }}
          >
            {t("whatshappening.table.total", "Total:")}
          </Typography>
          <Typography
            variant="h6"
            style={{ fontWeight: 700, color: "#000", marginLeft: "8px" }}
          >
            {formatNumber(totalKpiAllStages, unit, showUnitBefore)}
          </Typography>
        </Paper>
      </Box>
      {/* Drawer com as iniciativas do stage */}
      <Drawer
        anchor="bottom"
        open={stageDrawerOpen}
        onClose={handleCloseDrawer}
        BackdropProps={{ style: { backgroundColor: "#000", opacity: "0.1" } }}
        PaperProps={{
          style: {
            borderRadius: "10px",
            height: "80vh",
            width: "1200px",
            padding: "10px",
            margin: "1vh auto", // centraliza horizontalmente
          },
        }}
      >
        <Typography variant="h6" style={{ marginBottom: "12px" }}>
          {t("whatshappening.table.initiatives_in_stage")}{" "}
          <span style={{ textTransform: "capitalize" }}>{selectedStage}</span>
        </Typography>
        <PerfectScrollbar
          style={{ width: "100%", height: "calc(100% - 48px)" }}
          options={{ suppressScrollX: false }}
        >
          {stageInits.length > 0 ? (
            <>
              {stageInits
                .slice()
                .sort((a, b) => b.kpiValue - a.kpiValue)
                .map((init) => {
                  const isHovered = hoveredItem === init.id; // ← compara id
                  const isLoading = loadingInitId === init.id; // ← spinner só aqui

                  return (
                    <div
                      key={init.id}
                      style={{
                        marginBottom: "10px",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: isHovered
                          ? getAlphaColor(clientColor, 1)
                          : "transparent",
                      }}
                      onMouseEnter={() => setHoveredItem(init.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                      onClick={() => handleInitClickWrapper(init.id)}
                    >
                      {isLoading ? (
                        <>
                          <ThreeDots
                            color={userDetails.implementation_color}
                            height={20}
                            width={40}
                            ariaLabel="loading"
                          />
                          <Typography
                            variant="body2"
                            style={{
                              marginLeft: 8,
                              color: "#666",
                              fontStyle: "italic",
                            }}
                          >
                            {t("whatshappening.messages.loading_details")}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <div style={{ flexGrow: 1 }}>
                            <Typography style={{ fontWeight: 600 }}>
                              {init.initiativeName}
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{ color: "#666" }}
                            >
                              {t("whatshappening.labels.branch")} {init.branch}{" "}
                              | {t("whatshappening.labels.workfront")}{" "}
                              {init.workfront} |{" "}
                              {t("whatshappening.table.kpi_drawer")}
                              {formatNumber(
                                init.kpiValue,
                                unit,
                                showUnitBefore
                              )}
                            </Typography>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              <Box
                style={{
                  borderTop: "1px solid #ccc",
                  paddingTop: "8px",
                  marginTop: "16px",
                }}
              >
                <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                  {t("whatshappening.table.total_kpi")}
                  {formatNumber(
                    stageInits.reduce((sum, i) => sum + (i.kpiValue || 0), 0),
                    unit,
                    showUnitBefore
                  )}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body2" style={{ color: "#aaa" }}>
              {t("whatshappening.table.no_initiatives")}
            </Typography>
          )}
        </PerfectScrollbar>
      </Drawer>
    </div>
  );
}
StageOverview.propTypes = {
  initiatives: PropTypes.array.isRequired,
  stageChoices: PropTypes.array,
  unit: PropTypes.string,
  showUnitBefore: PropTypes.bool,
  initiativeDetailsOpen: PropTypes.bool,
};

// =============== DRILLDOWN VIEW ===============

/**
 * buildNestedStructure(kpiOverviewData)
 * => array de branches, cada branch tem array de workfronts, cada workfront tem array de iniciativas.
 * Ordenado desc pelo totalKpi.
 */
function buildNestedStructure(kpiOverviewData) {
  const mapBranches = {};

  for (const item of kpiOverviewData) {
    const {
      branch = "No Branch",
      workfront = "No Workfront",
      initiativeName = "No Initiative",
      kpiValue = 0,
    } = item;

    if (!mapBranches[branch]) {
      mapBranches[branch] = { totalKpi: 0, workfronts: {} };
    }
    mapBranches[branch].totalKpi += kpiValue;

    if (!mapBranches[branch].workfronts[workfront]) {
      mapBranches[branch].workfronts[workfront] = {
        totalKpi: 0,
        initiatives: {},
      };
    }
    mapBranches[branch].workfronts[workfront].totalKpi += kpiValue;

    if (
      !mapBranches[branch].workfronts[workfront].initiatives[initiativeName]
    ) {
      mapBranches[branch].workfronts[workfront].initiatives[initiativeName] = {
        totalKpi: 0,
        id: item.id || item.initiativeId || initiativeName, // usa id real; fallback no nome
      };
    }
    mapBranches[branch].workfronts[workfront].initiatives[
      initiativeName
    ].totalKpi += kpiValue;
  }

  // Converte map -> array
  const branchesArray = [];
  for (const bName of Object.keys(mapBranches)) {
    const { totalKpi, workfronts } = mapBranches[bName];
    const wfArray = [];
    for (const wfName of Object.keys(workfronts)) {
      const wfData = workfronts[wfName];
      const initArray = [];
      for (const iName of Object.keys(wfData.initiatives)) {
        const { totalKpi, id } = wfData.initiatives[iName]; // totalKpi e id
        initArray.push({
          id, // <-- inclui o id real
          initiative: iName,
          totalKpi,
        });
      }
      // ordena iniciativas desc
      initArray.sort((a, b) => b.totalKpi - a.totalKpi);

      wfArray.push({
        workfront: wfName,
        totalKpi: wfData.totalKpi,
        initiatives: initArray,
      });
    }
    // ordena workfront desc
    wfArray.sort((a, b) => b.totalKpi - a.totalKpi);

    branchesArray.push({
      branch: bName,
      totalKpi,
      workfronts: wfArray,
    });
  }
  // ordena branch desc
  branchesArray.sort((a, b) => b.totalKpi - a.totalKpi);

  return branchesArray;
}

/**
 * SavingDrilldownView
 *  - "viewMode" => "branch" | "workfront" | "initiative"
 *  - arrows for up/down => global switch
 *  - manual drilldown inside "branch" mode by clicking items
 */
function SavingDrilldownView({
  kpiOverviewData = [],
  unit = "",
  showUnitBefore = false,
  clientColor = "#2196f3",
  onInitiativeClick,
}) {
  const { t } = useTranslation();
  // 3 modos globais
  const [viewMode, setViewMode] = useState("branch");

  // Seleção manual (apenas no modo "branch")
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedWorkfront, setSelectedWorkfront] = useState(null);

  // Montar a hierarquia
  const branchesData = useMemo(
    () => buildNestedStructure(kpiOverviewData),
    [kpiOverviewData]
  );

  // total geral
  const totalAcrossAll = useMemo(() => {
    let sum = 0;
    for (const br of branchesData) sum += br.totalKpi;
    return sum;
  }, [branchesData]);

  // allWorkfronts (para modo "workfront")
  const allWorkfronts = useMemo(() => {
    // ex: { label, totalKpi, branch, workfront }
    const arr = [];
    for (const br of branchesData) {
      for (const wf of br.workfronts) {
        arr.push({
          label: `${wf.workfront}`,
          totalKpi: wf.totalKpi,
          branch: br.branch,
          workfront: wf.workfront,
        });
      }
    }
    arr.sort((a, b) => b.totalKpi - a.totalKpi);
    return arr;
  }, [branchesData]);

  // allInitiatives (para modo "initiative")
  const allInitiatives = useMemo(() => {
    const arr = [];
    for (const br of branchesData) {
      for (const wf of br.workfronts) {
        for (const init of wf.initiatives) {
          arr.push({
            id: init.id, // garante que o id exista
            label: `${init.initiative}`,
            totalKpi: init.totalKpi,
            branch: br.branch,
            workfront: wf.workfront,
          });
        }
      }
    }
    arr.sort((a, b) => b.totalKpi - a.totalKpi);
    return arr;
  }, [branchesData]);

  // qual branch + workfront está selecionado
  const selectedBranchObj = selectedBranch
    ? branchesData.find((b) => b.branch === selectedBranch)
    : null;
  const selectedWorkfrontObj = useMemo(() => {
    if (!selectedBranchObj || !selectedWorkfront) return null;
    return selectedBranchObj.workfronts.find(
      (wf) => wf.workfront === selectedWorkfront
    );
  }, [selectedBranchObj, selectedWorkfront]);

  // mouse‑over e spinner no item de iniciativa
  const [hoveredItem, setHoveredItem] = useState(null);
  const [loadingInitId, setLoadingInitId] = useState(null);

  // =============== BOTÕES DE SETA ==================
  // arrowDown => "desce" um nível global, se possível
  const handleArrowDown = () => {
    if (viewMode === "branch") {
      // se está no branch, passamos p/ "workfront"
      setViewMode("workfront");
      // reset manual selection
      setSelectedBranch(null);
      setSelectedWorkfront(null);
    } else if (viewMode === "workfront") {
      // passamos p/ "initiative"
      setViewMode("initiative");
      setSelectedBranch(null);
      setSelectedWorkfront(null);
    } else {
      // se já está em initiative, não dá p/ descer mais => ignore ou desabilite
    }
  };

  // arrowUp => "sobe" um nível global, se possível
  // se estamos em modo "branch", podemos estar no drilldown manual
  // subimos do selectedWorkfront -> sem selectedWorkfront, ou se não tem, subimos da selectedBranch -> sem selectedBranch
  // se no final, não tem nada selecionado, não tem p/ onde subir => ou se no "branch" top, disable
  // se no "workfront"/"initiative", subimos p/ a anterior
  const handleArrowUp = () => {
    if (viewMode === "initiative") {
      setViewMode("workfront");
      setSelectedBranch(null);
      setSelectedWorkfront(null);
    } else if (viewMode === "workfront") {
      setViewMode("branch");
      setSelectedBranch(null);
      setSelectedWorkfront(null);
    } else {
      // = branch => check se tem workfront selecionado, se sim desmarcar
      if (selectedWorkfront) {
        setSelectedWorkfront(null);
      } else if (selectedBranch) {
        setSelectedBranch(null);
      } else {
        // já está no top-level do branch => não tem p/ onde subir => ignore ou desabilite
      }
    }
  };

  // =============== DRILLDOWN MANUAL (cliques) ===============
  const handleBranchClick = (branchItem) => {
    if (viewMode !== "branch") return; // only if in "branch" mode
    setSelectedBranch(branchItem.branch);
    setSelectedWorkfront(null);
  };

  const handleWorkfrontClick = (wfItem) => {
    setSelectedWorkfront(wfItem.workfront);
  };

  // Para "All Workfronts": clique e ir direto p/ ver iniciativas
  const handleAllWorkfrontClick = (wfItem) => {
    // mudar p/ "branch" e setar a branch e workfront
    setViewMode("branch");
    setSelectedBranch(wfItem.branch);
    setSelectedWorkfront(wfItem.workfront);
  };

  const handleBackFromBranch = () => {
    setSelectedBranch(null);
    setSelectedWorkfront(null);
  };

  const handleBackFromWorkfront = () => {
    setSelectedWorkfront(null);
  };

  const handleInitClickWrapper = async (initId) => {
    setLoadingInitId(initId); // mostra spinner
    if (onInitiativeClick) {
      const maybePromise = onInitiativeClick(initId);
      if (maybePromise && typeof maybePromise.then === "function") {
        try {
          await maybePromise; // espera carregar
        } finally {
          setLoadingInitId(null); // some spinner
        }
      } else {
        setLoadingInitId(null);
      }
    } else {
      setLoadingInitId(null);
    }
  };

  // =============== STYLES ===============
  const useStyles = makeStyles(() => ({
    container: {
      padding: 16,
      overflowX: "auto",
    },
    topRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 30,
      marginTop: 20,
      marginRight: 15,
      gap: 8,
    },
    barRow: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      borderBottom: "1px dotted #ccc",
      paddingBottom: 8,
      marginBottom: 8,
      cursor: "pointer",
      "&:last-child": {
        borderBottom: "none",
        marginBottom: 0,
        paddingBottom: 0,
      },
    },
    // label com largura fixa p/ alinhar
    barLabel: {
      width: 220, // FIXO
      marginRight: 8,
      fontWeight: 500,
      fontSize: "0.88rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    bar: {
      padding: "14px 0",
      borderRadius: 6,
      boxShadow: "0 0 6px rgba(0,0,0,0.2)",
      transition: "width 0.3s ease-in-out",
      backgroundColor: "#ccc", // fallback
      "&:hover": {
        opacity: 0.85,
      },
    },
    barValue: {
      marginLeft: 8,
      minWidth: 90,
      textAlign: "right",
      fontWeight: 500,
      fontSize: "0.8rem",
      whiteSpace: "nowrap",
    },
    subTitle: {
      marginTop: 16,
      marginBottom: 8,
      fontWeight: 600,
      fontSize: "0.9rem",
    },
    totalWrapper: {
      display: "inline-block",
      marginLeft: "auto",
      marginTop: 12,
      padding: "4px 12px",
      borderRadius: 8,
      background: "linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      fontWeight: 600,
      color: "#333",
    },
    cardButton: {
      padding: "0 !important",
      margin: "0 !important",
      height: "30px !important",
      width: "30px !important",
      minWidth: "0 !important",
      position: "relative",
      transition: "all 0.2s",
    },
    cardButtonIcon: {
      margin: "0 !important",
      position: "absolute !important",
      top: "50% !important",
      left: "50% !important",
      transform: "translate(-50%, -50%)",
      width: "18px !important",
      height: "18px !important",
    },
  }));
  const classes = useStyles();

  const colorObj = parseHexColor(clientColor);

  const r = colorObj.r;
  const g = colorObj.g;
  const b = colorObj.b;
  const barBgColor = `rgba(${r}, ${g}, ${b}, 0.4)`;

  // Aux p/ exibir arrays { label, totalKpi } com clique
  function renderBarList(dataArray, totalOfAll, onItemClick) {
    const MAX_WIDTH = 600;
    return dataArray.map((item) => {
      const fraction = totalOfAll === 0 ? 0 : item.totalKpi / totalOfAll;
      const barWidth = Math.round(fraction * MAX_WIDTH);
      return (
        <div
          key={item.label}
          className={classes.barRow}
          onClick={() => onItemClick && onItemClick(item)}
        >
          <Tippy content={item.label} theme="light">
            <div className={classes.barLabel}>{item.label}</div>
          </Tippy>
          <div
            className={classes.bar}
            style={{ width: `${barWidth}px`, backgroundColor: barBgColor }}
          />
          <div className={classes.barValue}>
            {formatNumber(item.totalKpi, unit, showUnitBefore)}
          </div>
        </div>
      );
    });
  }

  // =============== RENDER ===============
  return (
    <div className={classes.container}>
      {/* Top bar with Title and arrows */}
      <div className={classes.topRow}>
        <div
          style={{
            position: "absolute",
            right: "15px",
            top: "15px",
            zIndex: 1000,
            display: "flex",
            marginBttom: "20px",
            gap: 8,
          }}
        >
          {/* Botão de Subir */}
          <Tippy
            content={t("whatshappening.buttons.up_hierarchy")}
            arrow
            theme="light"
          >
            <Button
              color="client"
              size="sm"
              variant="outlined"
              onClick={handleArrowUp}
              className={classes.cardButton}
            >
              <FontAwesomeIcon
                icon={faArrowUp}
                className={classes.cardButtonIcon}
              />
            </Button>
          </Tippy>

          {/* Botão de Descer */}
          <Tippy
            content={t("whatshappening.buttons.down_hierarchy")}
            arrow
            theme="light"
          >
            <Button
              color="client"
              size="sm"
              variant="outlined"
              onClick={handleArrowDown}
              className={classes.cardButton}
            >
              <FontAwesomeIcon
                icon={faArrowDown}
                className={classes.cardButtonIcon}
              />
            </Button>
          </Tippy>
        </div>
      </div>

      {/* Aqui renderizamos de acordo com viewMode */}
      {viewMode === "branch" ? (
        // ==================== MODO "BRANCH" + DRILLDOWN MANUAL ====================
        <>
          {!selectedBranch ? (
            // (1) Top level: listar todas as Branches
            <>
              {branchesData.map((br) => {
                const frac =
                  totalAcrossAll === 0 ? 0 : br.totalKpi / totalAcrossAll;
                const barWidth = Math.round(frac * 600);
                const isHovered = hoveredItem === br.branch;
                return (
                  <div
                    key={br.branch}
                    className={classes.barRow}
                    onClick={() => handleBranchClick(br)}
                    onMouseEnter={() => setHoveredItem(br.branch)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <Tippy content={br.branch} theme="light">
                      <div
                        className={classes.barLabel}
                        style={{ color: isHovered ? clientColor : "#333" }}
                      >
                        {br.branch}
                      </div>
                    </Tippy>
                    <div
                      className={classes.bar}
                      style={{
                        width: `${barWidth}px`,
                        backgroundColor: barBgColor,
                      }}
                    />
                    <div className={classes.barValue}>
                      {formatNumber(br.totalKpi, unit, showUnitBefore)}
                    </div>
                  </div>
                );
              })}
              <Box mt={1} display="flex" justifyContent="flex-end">
                <Paper
                  elevation={2}
                  style={{
                    padding: "4px 12px", // Modern, compact padding
                    borderRadius: "8px",
                    minWidth: "150px",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)", // Subtle gradient
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 600, color: "#333" }}
                  >
                    {t("whatshappening.table.total", "Total:")}
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{
                      fontWeight: 700,
                      color: "#000",
                      marginLeft: "8px",
                    }}
                  >
                    {formatNumber(totalAcrossAll, unit, showUnitBefore)}
                  </Typography>
                </Paper>
              </Box>
            </>
          ) : !selectedWorkfront ? (
            // (2) Branch selecionada, listar workfronts
            <>
              <div style={{ marginBottom: 5, marginTop: "-20px" }}>
                <Tippy
                  content={t("whatshappening.buttons.back_branch")}
                  arrow
                  theme="light"
                >
                  <Button
                    color="client"
                    size="sm"
                    variant="outlined"
                    onClick={handleBackFromBranch}
                    className={classes.cardButton}
                  >
                    <FontAwesomeIcon
                      icon={faArrowLeft}
                      className={classes.cardButtonIcon}
                    />
                  </Button>
                </Tippy>
              </div>
              <div className={classes.subTitle}>
                {t("whatshappening.labels.workfronts_in_branch")}{" "}
                <strong>{selectedBranchObj?.branch}</strong>
              </div>
              {selectedBranchObj?.workfronts.map((wf) => {
                const frac = selectedBranchObj?.totalKpi
                  ? wf.totalKpi / selectedBranchObj.totalKpi
                  : 0;
                const barWidth = Math.round(frac * 600);
                const isHovered = hoveredItem === wf.workfront;
                return (
                  <div
                    key={wf.workfront}
                    className={classes.barRow}
                    onClick={() => handleWorkfrontClick(wf)}
                    onMouseEnter={() => setHoveredItem(wf.workfront)}
                    onMouseLeave={() => setHoveredItem(null)}
                    style={{ cursor: "pointer" }}
                  >
                    <Tippy content={wf.workfront} theme="light">
                      <div
                        className={classes.barLabel}
                        style={{ color: isHovered ? clientColor : "#333" }}
                      >
                        {wf.workfront}
                      </div>
                    </Tippy>
                    <div
                      className={classes.bar}
                      style={{
                        width: `${barWidth}px`,
                        backgroundColor: barBgColor,
                      }}
                    />
                    <div className={classes.barValue}>
                      {formatNumber(wf.totalKpi, unit, showUnitBefore)}
                    </div>
                  </div>
                );
              })}
              <Box mt={1} display="flex" justifyContent="flex-end">
                <Paper
                  elevation={2}
                  style={{
                    padding: "4px 12px", // Modern, compact padding
                    borderRadius: "8px",
                    minWidth: "150px",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)", // Subtle gradient
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 600, color: "#333" }}
                  >
                    {t("whatshappening.table.total", "Total:")}
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{
                      fontWeight: 700,
                      color: "#000",
                      marginLeft: "8px",
                    }}
                  >
                    {formatNumber(
                      selectedBranchObj?.totalKpi,
                      unit,
                      showUnitBefore
                    )}
                  </Typography>
                </Paper>
              </Box>
            </>
          ) : (
            // (3) Workfront selecionado => listar iniciativas
            <>
              <div style={{ marginBottom: 16 }}>
                <Tippy
                  content={t("whatshappening.buttons.back_workfront")}
                  arrow
                  theme="light"
                >
                  <Button
                    color="client"
                    size="sm"
                    variant="outlined"
                    onClick={handleBackFromWorkfront}
                    className={classes.cardButton}
                  >
                    <FontAwesomeIcon
                      icon={faArrowLeft}
                      className={classes.cardButtonIcon}
                    />
                  </Button>
                </Tippy>
              </div>
              <div className={classes.subTitle}>
                {t("whatshappening.labels.initiatives_in_workfront")}{" "}
                <strong>{selectedWorkfront}</strong>
              </div>
              {selectedWorkfrontObj?.initiatives.map((init) => {
                const frac = selectedWorkfrontObj?.totalKpi
                  ? init.totalKpi / selectedWorkfrontObj.totalKpi
                  : 0;
                const barWidth = Math.round(frac * 600);

                const isHovered = hoveredItem === init.id;
                const isLoading = loadingInitId === init.id;

                return (
                  <div
                    key={init.initiative}
                    className={classes.barRow}
                    onMouseEnter={() => setHoveredItem(init.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                    onClick={() => handleInitClickWrapper(init.id)}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    {isLoading ? (
                      <>
                        <ThreeDots
                          color={clientColor}
                          height={20}
                          width={40}
                          ariaLabel="loading"
                        />
                        <Typography
                          variant="body2"
                          style={{
                            marginLeft: 8,
                            fontStyle: "italic",
                            color: "#666",
                          }}
                        >
                          {t("whatshappening.messages.loading_details")}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Tippy content={init.initiative} theme="light">
                          <div
                            className={classes.barLabel}
                            style={{ color: isHovered ? clientColor : "#333" }}
                          >
                            {init.initiative}
                          </div>
                        </Tippy>
                        <div
                          className={classes.bar}
                          style={{
                            width: `${barWidth}px`,
                            backgroundColor: barBgColor,
                          }}
                        />
                        <div className={classes.barValue}>
                          {formatNumber(init.totalKpi, unit, showUnitBefore)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
              <Box mt={1} display="flex" justifyContent="flex-end">
                <Paper
                  elevation={2}
                  style={{
                    padding: "4px 12px", // Modern, compact padding
                    borderRadius: "8px",
                    minWidth: "150px",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)", // Subtle gradient
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 600, color: "#333" }}
                  >
                    {t("whatshappening.table.total", "Total:")}
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{
                      fontWeight: 700,
                      color: "#000",
                      marginLeft: "8px",
                    }}
                  >
                    {formatNumber(
                      selectedWorkfrontObj?.totalKpi,
                      unit,
                      showUnitBefore
                    )}
                  </Typography>
                </Paper>
              </Box>
            </>
          )}
        </>
      ) : viewMode === "workfront" ? (
        // ==================== ALL WORKFRONTS (DESC) ====================
        <>
          {renderBarList(allWorkfronts, totalAcrossAll, (wfItem) =>
            handleAllWorkfrontClick(wfItem)
          )}
          <Box mt={1} display="flex" justifyContent="flex-end">
            <Paper
              elevation={2}
              style={{
                padding: "4px 12px", // Modern, compact padding
                borderRadius: "8px",
                minWidth: "150px",
                background: "linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)", // Subtle gradient
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 600, color: "#333" }}
              >
                {t("whatshappening.table.total", "Total:")}
              </Typography>
              <Typography
                variant="h6"
                style={{ fontWeight: 700, color: "#000", marginLeft: "8px" }}
              >
                {formatNumber(totalAcrossAll, unit, showUnitBefore)}
              </Typography>
            </Paper>
          </Box>
        </>
      ) : (
        // ==================== ALL INITIATIVES (DESC) ====================
        <>
          {/* Lista global de iniciativas com hover/spinner */}
          {allInitiatives.map((init) => {
            const frac =
              totalAcrossAll === 0 ? 0 : init.totalKpi / totalAcrossAll;
            const barWidth = Math.round(frac * 600);
            const isHovered = hoveredItem === init.id;
            const isLoading = loadingInitId === init.id;

            return (
              <div
                key={init.id}
                className={classes.barRow}
                onMouseEnter={() => setHoveredItem(init.id)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => handleInitClickWrapper(init.id)}
                style={{
                  cursor: "pointer",
                }}
              >
                {isLoading ? (
                  <>
                    <ThreeDots
                      color={clientColor}
                      height={20}
                      width={40}
                      ariaLabel="loading"
                    />
                    <Typography
                      variant="body2"
                      style={{
                        marginLeft: 8,
                        fontStyle: "italic",
                        color: "#666",
                      }}
                    >
                      {t("whatshappening.messages.loading_details")}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Tippy content={init.label} theme="light">
                      <div className={classes.barLabel}>{init.label}</div>
                    </Tippy>
                    <div
                      className={classes.bar}
                      style={{
                        width: `${barWidth}px`,
                        backgroundColor: barBgColor,
                      }}
                    />
                    <div className={classes.barValue}>
                      {formatNumber(init.totalKpi, unit, showUnitBefore)}
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <Box mt={1} display="flex" justifyContent="flex-end">
            <Paper
              elevation={2}
              style={{
                padding: "4px 12px", // Modern, compact padding
                borderRadius: "8px",
                minWidth: "150px",
                background: "linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)", // Subtle gradient
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 600, color: "#333" }}
              >
                {t("whatshappening.table.total", "Total:")}
              </Typography>
              <Typography
                variant="h6"
                style={{ fontWeight: 700, color: "#000", marginLeft: "8px" }}
              >
                {formatNumber(totalAcrossAll, unit, showUnitBefore)}
              </Typography>
            </Paper>
          </Box>
        </>
      )}
    </div>
  );
}

// =============== ROW COMPONENT ===============
function StatusRow({
  row,
  level = 0,
  unit = "",
  showUnitBefore = false,
  clientColor,
  classes,
  hoveredInitId,
  setHoveredInitId,
  loadingInitId,
  handleInitClickWrapper,
}) {
  const [open, setOpen] = useState(false);
  const isInitiative = !row.children || row.children.length === 0; // ↙ novo
  const isHovered = isInitiative && hoveredInitId === row.id;
  const isLoading = isInitiative && loadingInitId === row.id;
  const { t } = useTranslation();

  return (
    <>
      <TableRow
        className={level === 0 ? classes.tableRow : classes.childRow}
        style={{
          backgroundColor: getAlphaColor(clientColor, level),
          cursor: isInitiative ? "pointer" : "default",
        }}
        onClick={() => isInitiative && handleInitClickWrapper(row.id)}
      >
        {/* Expand Icon */}
        <TableCell className={classes.tableCell}>
          {row.children && row.children.length > 0 && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        {/* Name Cell With Indentation */}
        <TableCell
          className={`${classes.tableCell} ${classes.ellipsisCell}`}
          style={{ width: 230 }}
          onMouseEnter={() => isInitiative && setHoveredInitId(row.id)}
          onMouseLeave={() => isInitiative && setHoveredInitId(null)}
        >
          {isLoading ? (
            <div style={{ display: "flex", alignItems: "center" }}>
              <ThreeDots
                color={clientColor}
                height={20}
                width={40}
                ariaLabel="loading"
              />
              <Typography
                variant="body2"
                style={{ marginLeft: 8, fontStyle: "italic", color: "#666" }}
              >
                {t("whatshappening.messages.loading_details")}
              </Typography>
            </div>
          ) : (
            <Tippy content={row.name} theme="light">
              <span
                style={{
                  paddingLeft: level * 5,
                  display: "inline-block",
                  width: "100%",
                  color: isHovered ? clientColor : "inherit",
                }}
              >
                {row.name}
              </span>
            </Tippy>
          )}
        </TableCell>
        {/* Number of initiatives */}
        <TableCell align="center" className={classes.tableCell}>
          {formatNumber(row.initiatives)}
        </TableCell>

        <TableCell align="right" className={classes.tableCell}>
          {formatNumber(row.kpiValue, unit, showUnitBefore)}
        </TableCell>

        <TableCell align="right" className={classes.tableCell}>
          {formatNumber(row.kpiCapturedYTD || 0, unit, showUnitBefore)}
        </TableCell>
        {/* % YTD => kpiCapturedYTD / kpiTargetYTD * 100 */}
        <TableCell align="center" className={classes.tableCell}>
          {(() => {
            const meta = row.kpiTargetYTD || 0;
            const capturado = row.kpiCapturedYTD || 0;
            if (meta === 0) return "0%";
            const ratio = (capturado / meta) * 100;
            const ratioDisplay = `${ratio.toFixed(0)}%`;
            return (
              <Tippy
                theme="light"
                content={`Capturado: ${formatNumber(capturado, unit, showUnitBefore)} | Meta: ${formatNumber(meta, unit, showUnitBefore)}`}
              >
                <span
                  style={{
                    color: ratio > 80 ? "green" : "inherit",
                    cursor: "default",
                  }}
                >
                  {ratioDisplay}
                </span>
              </Tippy>
            );
          })()}
        </TableCell>

        {/* Status circle */}
        <TableCell align="center" className={classes.tableCell}>
          <span
            className={`${classes.hoverCell} ${
              isHovered && !isLoading ? "filling" : ""
            }`}
            onMouseEnter={() => isInitiative && setHoveredInitId(row.id)}
            onMouseLeave={() => isInitiative && setHoveredInitId(null)}
            style={getStatusStyles(row.status, isHovered)}
          />
        </TableCell>
      </TableRow>

      {row.children && row.children.length > 0 && (
        <TableRow>
          <TableCell
            colSpan={7}
            style={{ paddingBottom: 0, paddingTop: 0 }}
            className={classes.tableCell}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box margin={1}>
                <Table size="small" aria-label="child-rows">
                  <TableBody>
                    {row.children.map((child) => (
                      <StatusRow // ⬅️  novo nome
                        key={child.name}
                        row={child}
                        level={level + 1}
                        unit={unit}
                        showUnitBefore={showUnitBefore}
                        clientColor={clientColor}
                        classes={classes}
                        hoveredInitId={hoveredInitId}
                        setHoveredInitId={setHoveredInitId}
                        loadingInitId={loadingInitId}
                        handleInitClickWrapper={handleInitClickWrapper}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
StatusRow.propTypes = {
  row: PropTypes.shape({
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    kpiValue: PropTypes.number.isRequired,
    initiatives: PropTypes.number.isRequired,
    kpiCapturedYTD: PropTypes.number,
    kpiTargetYTD: PropTypes.number,
    children: PropTypes.array,
  }).isRequired,
  classes: PropTypes.object.isRequired,
  level: PropTypes.number,
  unit: PropTypes.string,
  showUnitBefore: PropTypes.bool,
};

// =============== MAIN COMPONENT ===============
export default function CollapsibleStatusTable(props) {
  const {
    viewModeOverview,
    kpiOverviewData,
    stageChoices,
    selectedSection,
    unit = "",
    showUnitBefore = false,
    onInitiativeClick,
    initiativeDetailsOpen = false,
  } = props;

  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#2196f3";
  const { t } = useTranslation();

  // =========== STYLES
  const useDynamicStyles = (clientColor) => ({
    // ====== Estilos para o efeito "fill" quando passa o mouse no círculo de status ======
    hoverCell: {
      position: "relative",
      overflow: "hidden",
      cursor: "pointer",
      "&::before": {
        content: "''",
        position: "absolute",
        top: 0,
        left: 0,
        width: "0%",
        height: "100%",
        backgroundColor: clientColor
          ? `${clientColor}1A`
          : "rgba(33, 150, 243, 0.05)",
        transition: "width 1s linear",
      },
      "&.filling::before": {
        animation: "$fillCell 1s linear forwards",
      },
    },

    "@keyframes fillCell": {
      "0%": { width: "0%" },
      "100%": { width: "100%" },
    },

    // ====== Ícones de sort ======
    sortIcon: {
      fontSize: "14px",
      marginLeft: "6px",
      color: "#ccc",
    },
    activeSortIcon: {
      color: "#aaa",
    },

    tableCell: {
      /* 
        'whiteSpace: normal' -> Do not respect literal newlines 
        'wordBreak' + 'overflowWrap' -> break long words gracefully
      */
      whiteSpace: "pre-wrap",
      overflowWrap: "break-word",
      wordBreak: "break-word",
      verticalAlign: "middle",
      lineHeight: 1.3,
      padding: "8px 16px",
    },
    ellipsisCell: {
      /* Força uma largura máxima para a célula de “nome” */
      maxWidth: 250, // ajuste conforme necessário
      whiteSpace: "nowrap", // impede quebrar em múltiplas linhas
      overflow: "hidden",
      textOverflow: "ellipsis", // mostra “...” quando extrapola
      verticalAlign: "middle",
    },
  });

  const useStyles = makeStyles((theme) => useDynamicStyles(theme, clientColor));
  const classes = useStyles();

  // Precisamos converter data => buildHierarchyFromInitiatives
  // Lembrando que a view do back envia:
  // {
  //   id, branch, workfront, initiativeName,
  //   status, stage,
  //   kpiValue, kpiCapturedYTD, kpiTargetYTD
  // }
  // E o buildHierarchyFromInitiatives espera as mesmas props. Então OK:
  const hierarchyData = buildHierarchyFromInitiatives(
    props.kpiOverviewData || []
  );

  const [hoveredInitId, setHoveredInitId] = useState(null);
  const [loadingInitId, setLoadingInitId] = useState(null);

  const handleInitClickWrapper = async (id) => {
    setLoadingInitId(id); // ativa spinner
    if (onInitiativeClick) {
      const maybePromise = onInitiativeClick(id);
      if (maybePromise && typeof maybePromise.then === "function") {
        try {
          await maybePromise; // espera carregar
        } finally {
          setLoadingInitId(null); // desliga spinner
        }
      } else {
        setLoadingInitId(null);
      }
    } else {
      setLoadingInitId(null);
    }
  };

  // =========== SORTING
  const [sortConfig, setSortConfig] = useState({
    key: "status",
    direction: "ascending",
  });
  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = sortDataRecursively(hierarchyData, sortConfig);

  return (
    <>
      {viewModeOverview === "table" ? (
        // ================= TABLE VIEW =================
        <TableContainer component={Paper}>
          <Table style={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell
                  onClick={() => handleSort("name")}
                  style={{ cursor: "pointer" }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {t("whatshappening.table.name")}
                    <FontAwesomeIcon
                      icon={
                        sortConfig.key === "name"
                          ? sortConfig.direction === "ascending"
                            ? faSortUp
                            : faSortDown
                          : faSort
                      }
                      className={
                        sortConfig.key === "name"
                          ? `${classes.sortIcon} ${classes.activeSortIcon}`
                          : classes.sortIcon
                      }
                    />
                  </div>
                </TableCell>
                <TableCell
                  align="center"
                  onClick={() => handleSort("initiatives")}
                  style={{ cursor: "pointer" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {t("whatshappening.table.number_initiatives")}
                    <FontAwesomeIcon
                      icon={
                        sortConfig.key === "initiatives"
                          ? sortConfig.direction === "ascending"
                            ? faSortUp
                            : faSortDown
                          : faSort
                      }
                      className={
                        sortConfig.key === "initiatives"
                          ? `${classes.sortIcon} ${classes.activeSortIcon}`
                          : classes.sortIcon
                      }
                    />
                  </div>
                </TableCell>
                <TableCell
                  align="right"
                  onClick={() => handleSort("kpiValue")}
                  style={{ cursor: "pointer" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    {t("whatshappening.table.table_kpi")}
                    <FontAwesomeIcon
                      icon={
                        sortConfig.key === "kpiValue"
                          ? sortConfig.direction === "ascending"
                            ? faSortUp
                            : faSortDown
                          : faSort
                      }
                      className={
                        sortConfig.key === "kpiValue"
                          ? `${classes.sortIcon} ${classes.activeSortIcon}`
                          : classes.sortIcon
                      }
                    />
                  </div>
                </TableCell>
                <TableCell align="right" style={{ minWidth: 120 }}>
                  {t("whatshappening.table.captured_ytd")}
                </TableCell>
                <TableCell align="center" style={{ minWidth: 90 }}>
                  % YTD
                </TableCell>
                <TableCell
                  align="center"
                  onClick={() => handleSort("status")}
                  style={{ cursor: "pointer" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {t("whatshappening.table.status")}
                    <FontAwesomeIcon
                      icon={
                        sortConfig.key === "status"
                          ? sortConfig.direction === "ascending"
                            ? faSortUp
                            : faSortDown
                          : faSort
                      }
                      className={
                        sortConfig.key === "status"
                          ? `${classes.sortIcon} ${classes.activeSortIcon}`
                          : classes.sortIcon
                      }
                    />
                  </div>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((branch) => (
                <StatusRow
                  key={branch.name}
                  row={branch}
                  level={0}
                  unit={unit}
                  showUnitBefore={showUnitBefore}
                  clientColor={clientColor}
                  classes={classes}
                  hoveredInitId={hoveredInitId}
                  setHoveredInitId={setHoveredInitId}
                  loadingInitId={loadingInitId}
                  handleInitClickWrapper={handleInitClickWrapper}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : viewModeOverview === "stages" ? (
        // ================= STAGE OVERVIEW =================
        <StageOverview
          initiatives={kpiOverviewData || []}
          stageChoices={stageChoices}
          unit={unit}
          showUnitBefore={showUnitBefore}
          onInitiativeClick={onInitiativeClick}
          initiativeDetailsOpen={initiativeDetailsOpen}
        />
      ) : (
        <SavingDrilldownView
          kpiOverviewData={kpiOverviewData || []}
          unit={unit}
          showUnitBefore={showUnitBefore}
          clientColor={clientColor}
          onInitiativeClick={onInitiativeClick}
        />
      )}
    </>
  );
}

CollapsibleStatusTable.propTypes = {
  selectedSection: PropTypes.object,
  viewModeOverview: PropTypes.oneOf(["table", "stages"]).isRequired,
  kpiOverviewData: PropTypes.array.isRequired,
  stageChoices: PropTypes.array, // New prop for stage choices
  unit: PropTypes.string,
  showUnitBefore: PropTypes.bool,
  initiativeDetailsOpen: PropTypes.bool,
};
